// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.
import "../../style/sassStyle";

import { ROUTE_DEFINITIONS, getPath } from "routes/routes";
import NextStepLink from "../TestRun/NextStepLink";
import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Theme,
  createStyles,
  makeStyles,
} from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cx } from "style/utils";
import { fbt } from "fbt";

import Divider from "components/ui/Divider";
import ExternalLink from "components/ui/ExternalLink";
import { Link, useParams } from "react-router-dom";
import PageContent from "../ui/PageContent";
import PageHeader from "../ui/PageHeader";
import { useTestRunState } from "store/testrun";
import { TestRunParams } from "../TestRun/TestRunConstants";

const NO_RESULT_YET = 0;
const ERROR = 1;
const NO_RDT = 2;
const POSITIVE = 3;
const NEGATIVE = 4;
const INVALID = 5;

type IconType =
  | "question-circle"
  | "plus-circle"
  | "minus-circle"
  | "exclamation-circle";

const useStyle = makeStyles((theme: Theme) =>
  createStyles({
    wrapperRoot: {
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      display: "flex",
      flexDirection: "column",
      backgroundColor: "rgba(0, 0, 0, .5)",
    },
    root: {
      margin: "auto",
    },
    icon: {
      color: "gray",
      float: "left",
      margin: ".2em",
    },
    testResultPill: {
      cursor: "default",
      margin: ".75em 0",
    },
    sars: {
      color: "grey",
    },
    testResultContainer: {
      alignItems: "center",
      display: "flex",
      margin: "auto",
      overflow: "auto",
    },
    nextButton: {
      minWidth: "185px",
      marginTop: "30px",
    },
    switch: {
      float: "right",
      margin: "30px",
      marginRight: "50px",
    },
    textButton: {
      textDecoration: "underline",
      cursor: "pointer",
    },
    bold: {
      fontWeight: "bold",
    },
  })
);

export default () => {
  const [result, setResult] = useState(NO_RESULT_YET);
  const { testRunUID } = useParams<TestRunParams>();
  const testRunState = useTestRunState();

  const styles = useStyle();

  useEffect(() => {
    if (testRunState === undefined) {
      return;
    }

    if (!testRunState?.testRun?.mlResult) {
      setResult(ERROR);
    } else if (!testRunState?.testRun?.mlResult.rdtDetected) {
      setResult(NO_RDT);
    } else {
      const interpretation = testRunState.testRun?.mlResult.interpretation;
      if (interpretation === "POSITIVE") {
        setResult(POSITIVE);
      } else if (interpretation === "NEGATIVE") {
        setResult(NEGATIVE);
      } else {
        setResult(INVALID);
      }
    }
  }, [testRunState]);

  if (testRunState === undefined) {
    return (
      <div className={styles.wrapperRoot}>
        <CircularProgress
          classes={{ root: styles.root }}
          size={150}
          variant={"indeterminate"}
        />
      </div>
    );
  }
  const scanPath = getPath(ROUTE_DEFINITIONS.PERFORMTEST, {
    params: {
      ":testRunUID": testRunUID,
      ":step": "scanStrip",
    },
  });

  const resultBox = (icon: IconType, message: string, footer: any) => {
    return (
      <>
        <div className={styles.testResultContainer}>
          <FontAwesomeIcon icon={icon} size="3x" className={styles.icon} />
          <div>
            <div className={styles.sars}>SARS</div>
            <strong>{message}</strong>
          </div>
        </div>
        {footer}
        <Divider />
      </>
    );
  };

  let content;
  switch (result) {
    case INVALID:
      const invalidText = (
        <span>
          Test is <strong>inconclusive</strong> for SARS.
        </span>
      );
      content = (
        <>
          {resultBox("question-circle", "INCONCLUSIVE", invalidText)}
          The test cassette appears to be invalid or defective. Please complete
          another test.
          <br />
        </>
      );
      break;
    case POSITIVE:
    case NEGATIVE:
      content = (
        <>
          {resultBox(
            result === POSITIVE ? "plus-circle" : "minus-circle",
            result === POSITIVE ? "POSITIVE" : "NEGATIVE",
            `Test indicates a valid ${
              result === POSITIVE ? "Positive" : "Negative"
            } result for SARS.`
          )}
          <ExternalLink href="https://www.cdc.gov/coronavirus/2019-ncov/if-you-are-sick/steps-when-sick.html">
            How to protect yourself and others.
          </ExternalLink>
          <br />
          <br />
          {result === POSITIVE ? (
            <>
              A positive test indicates that you have a likely infection. Please
              follow the guidance below to help stop the spread of COVID-19 to
              others.
              <br />
              <br />
              <strong>Stay home except to get medical care</strong>
              <br />
              You should stay home except to get medical care. Do not go to
              work, school, or public areas. Avoid using public transportation,
              ride-sharing, or taxis. Ask friends or family members to do your
              shopping or use a grocery delivery service.
              <br />
              <br />
              <strong>Take care of yourself</strong>
              <br />
              Get rest and stay hydrated. Take over-the-counter medicines, such
              as acetaminophen, to help you feel better.
              <br />
              <br />
              <strong>Call before you go to the doctor</strong>
              <br />
              Tell your health care provider you have COVID-19, or are being
              evaluated for COVID-19. Put on a face covering before you enter
              the building. These steps will help keep people in the office or
              waiting room from getting sick.
              <br />
              <br />
              <strong>Isolate yourself from people in your home</strong>
              <br />
              As much as possible, stay in a specific room away from other
              people and use a separate bathroom if available.
              <br />
              <br />
              <strong>Monitor your symptoms</strong>
              <br />
              Get medical help quickly if your symptoms get worse (if you have
              breathing trouble, etc.). If you have a medical emergency and need
              to call 911, tell the dispatcher that you have, or may have
              COVID-19. If possible, put on a face covering before emergency
              medical services arrive.
              <br />
              <br />
            </>
          ) : (
            <>
              A negative test indicates that you{" "}
              <strong>
                do not currently have an infection detectable by this test
              </strong>
              .
              <br />
              <br />
              Some people who are infected with COVID-19 will still get a
              negative test, as it can take between 2-10 or more days from
              initial infection to produce sufficient viral load to be detected
              by this test.
              <br />
              <br />
              <strong>
                It is important to understand that this test cannot rule out an
                active infection.
              </strong>
              Therefore, it is very important that you continue to protect
              yourself and others from the virus.
            </>
          )}
          <Divider />
          <div>
            <div>
              <strong>Medical Disclaimer</strong>
            </div>
            This communication of the SARS-COV-2 test result does not constitute
            professional medical or healthcare advice, diagnosis or
            recommendation of treatment, and is not intended to, nor should they
            be used to, replace professional medical advice.
            <br />
            The interpretation of your result may differ from a medical test
            conducted in a clinical lab environment. In no circumstances should
            the test results of the test kit be relied upon without independent
            consideration and confirmation by a qualified medical practitioner.
            <br />
            Audere makes no representations or warranties with respect to any
            treatment, action, suitability or application of medication, or
            preparation by any person, whether in connection with this site, the
            test kit, or not. In no circumstances will Audere be liable for any
            direct, indirect, consequential, specials, exemplary or other
            damages arising therefrom.
          </div>
        </>
      );
      break;
    case ERROR:
    case NO_RDT:
      const resultText =
        result === NO_RDT
          ? "We could not identify the test cassette in your photo."
          : "There was an error interpreting your test.";

      content = (
        <>
          {resultBox("exclamation-circle", "ERROR", resultText)}
          <div className={styles.testResultPill}>
            <span>Please go back and try again.</span>
          </div>
          <Link to={scanPath}>
            <div className={cx(styles.textButton, styles.bold)}>
              Retake photo
            </div>
          </Link>
        </>
      );
      break;
    case NO_RESULT_YET:
    default:
      content = (
        <div className={styles.testResultPill}>
          <strong>No result yet.</strong>
        </div>
      );
      break;
  }

  const onClick = () => {
    if (result === INVALID) {
      setResult(1);
    } else {
      setResult(result + 1);
    }
  };

  return (
    <>
      <div onClick={onClick} className={cx(styles.textButton, styles.switch)}>
        <fbt desc="Toggle result" project="audere">
          Toggle result
        </fbt>
      </div>
      <PageContent>
        <section className="section">
          <PageHeader title="Test Result" />
          <div className="container">
            {content}
            <br />
            <NextStepLink
              to={getPath(ROUTE_DEFINITIONS.CLEANUP)}
              floatLeft={true}
              large={true}
            />
          </div>
        </section>
      </PageContent>
    </>
  );
};
